import { FC, useEffect, useMemo, useState } from 'react';
import ReactMapGL, {
  Source,
  Layer,
  Marker,
  ViewState,
  NavigationControl,
  MapRef,
  ViewStateChangeEvent,
  MapProps,
  GeolocateControl,
  MapEvent,
} from 'react-map-gl/maplibre';
import bbox from '@turf/bbox';
import { MapPin } from '@phosphor-icons/react';
import { Icon } from '@chakra-ui/react';
import normalize from '@mapbox/geojson-normalize';
import { StyleSpecification } from 'maplibre-gl';
import {
  getBoundsForPoints,
  getPointsFromFeatureCollection,
} from '@/lib/mapUtils';
import 'maplibre-gl/dist/maplibre-gl.css';
import { MML_MAP_STYLE_TAUSTAKARTTA } from '@/lib/mml';
import { fillStyle, lineStyle } from '../common';
import voidFn from '@/lib/voidFn';

interface ReactMapProps {
  geoJson?: GeoJSON.GeoJSON;
  dimensions?: DOMRect | null;
  showNavigationControl?: boolean;
  showCenterMarker?: boolean;
  showUserLocation?: boolean;
  hidePointMarkers?: boolean;
  scrollZoom?: boolean;
  touchZoom?: boolean;
  doubleClickZoom?: boolean;
  initialViewState?: Partial<ViewState>;
  onMove?: (event: ViewStateChangeEvent) => void;
  onRender?: (event: MapEvent) => void;
  mapRef?: React.RefObject<MapRef> | React.RefCallback<MapRef>;
  cooperativeGestures?: MapProps['cooperativeGestures'];
  mapStyle?: StyleSpecification;
  boundsPadding?: number;
}

/**
 * General Map component for displaying GeoJSON featureCollections / features / geometries.
 *
 * @returns
 */
const ReactMap: FC<ReactMapProps> = ({
  geoJson,
  dimensions,
  showNavigationControl,
  showCenterMarker,
  showUserLocation,
  hidePointMarkers,
  initialViewState,
  onMove = voidFn,
  onRender = voidFn,
  mapRef,
  mapStyle = MML_MAP_STYLE_TAUSTAKARTTA,
  boundsPadding = 100,
  children,
  ...rest
}) => {
  // Since the passed GeoJSON can be anything from a simple Geometry (only type and coordinates)
  // to a Feature or even a FeatureCollection, we normalize the passed data so we're always
  // dealing with a FeatureCollection.
  const featureCollection = useMemo(() => normalize(geoJson), [geoJson]);

  // Try to get some sane bounds for the viewport based on the passed GeoJSON data
  // (tries to fit everything into the viewport). If the passed data only contains
  // a Point (as opposed to polygons i.e. areas), then the zoom will be way too close.
  // We try to set a reasonable zoom level in this case, but it's better to supply
  // a custom zoom level in the `initialViewState` prop.
  const viewStateBounds = useMemo(() => {
    if (!featureCollection || !featureCollection.features.length) {
      return undefined;
    }

    const boundingBox = bbox(featureCollection);
    const bounds = getBoundsForPoints(
      boundingBox as [number, number, number, number],
      {
        width: dimensions?.width || 600,
        height: dimensions?.height || 600,
      },
      boundsPadding,
    );

    // Don't zoom in too close
    bounds.zoom = Math.min(15, bounds.zoom);

    return bounds;
  }, [boundsPadding, dimensions?.height, dimensions?.width, featureCollection]);

  // Our normalized FeatureCollection may contain either Points or Polygons
  // (or both or something else). Extract the Points, since we show them as
  // Markers by default. Polygons, on the other hand, are rendered using Source and Layers.
  const points = useMemo(
    () => getPointsFromFeatureCollection(featureCollection),
    [featureCollection],
  );

  const [viewState, setViewState] = useState({
    ...viewStateBounds,
    ...initialViewState,
  });

  useEffect(() => {
    if (viewStateBounds) {
      setViewState({ ...viewStateBounds });
    }
  }, [viewStateBounds]);

  return (
    <ReactMapGL
      ref={mapRef}
      {...viewState}
      mapStyle={mapStyle}
      style={{
        width: '100%',
        height: '100%',
      }}
      onRender={onRender}
      onMove={(event) => {
        setViewState(event.viewState);
        onMove(event);
      }}
      {...rest}
    >
      {showNavigationControl && (
        <NavigationControl style={{ right: 10, top: 10 }} />
      )}
      {!hidePointMarkers &&
        points.map((point) => (
          <Marker
            key={point.coordinates.join(':')}
            latitude={point.coordinates[1]}
            longitude={point.coordinates[0]}
            offset={[0, -12]}
          >
            <Icon
              color="success.500"
              weight="fill"
              as={MapPin}
              boxSize="36px"
            />
          </Marker>
        ))}

      {showCenterMarker && viewState.latitude && viewState.longitude ? (
        <Marker
          key="centerMarker"
          latitude={viewState.latitude}
          longitude={viewState.longitude}
          offset={[0, -12]}
        >
          <Icon color="success.500" weight="fill" as={MapPin} boxSize="36px" />
        </Marker>
      ) : null}

      {showUserLocation && (
        <GeolocateControl
          style={{ marginRight: '31px', marginTop: '65px' }}
          positionOptions={{ enableHighAccuracy: true }}
          trackUserLocation
          position="top-right"
          onTrackUserLocationEnd={() => setViewState({ ...viewStateBounds })}
        />
      )}

      <Source id="my-data" type="geojson" data={featureCollection}>
        <Layer {...fillStyle} />
        <Layer {...lineStyle} />
      </Source>
      {children}
    </ReactMapGL>
  );
};

export default ReactMap;
